import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { RecordingProvider } from "@/contexts/RecordingContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { HelmetProvider } from "react-helmet-async";
import { TrackingScriptsLoader } from "@/components/TrackingScriptsLoader";
import { RouteChangeTracker } from "@/components/RouteChangeTracker";
import { RecordingBar } from "@/components/consultations/RecordingBar";
// Lazy load pages for code splitting - improves FCP
const LandingPage = lazy(() => import("./pages/LandingPage"));
const LandingPageLegacy = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Cadastro = lazy(() => import("./pages/Cadastro"));
const EsqueciSenha = lazy(() => import("./pages/EsqueciSenha"));
const RedefinirSenha = lazy(() => import("./pages/RedefinirSenha"));
const Privacidade = lazy(() => import("./pages/Privacidade"));
const TermosUso = lazy(() => import("./pages/TermosUso"));
const LGPD = lazy(() => import("./pages/LGPD"));
const OfertaIndicacao = lazy(() => import("./pages/OfertaIndicacao"));
const ReferralRedirect = lazy(() => import("./pages/ReferralRedirect"));
const Ajuda = lazy(() => import("./pages/Ajuda"));
const Status = lazy(() => import("./pages/Status"));
const BlogIndex = lazy(() => import("./pages/Blog/Index"));
const BlogPost = lazy(() => import("./pages/Blog/Post"));
const Consultas = lazy(() => import("./pages/Consultas"));
const Templates = lazy(() => import("./pages/Templates"));
const Plano = lazy(() => import("./pages/Plano"));
const Perfil = lazy(() => import("./pages/Perfil"));
const Dispositivos = lazy(() => import("./pages/Dispositivos"));
const Admin = lazy(() => import("./pages/Admin"));
const Prontuario = lazy(() => import("./pages/Prontuario"));
const Companion = lazy(() => import("./pages/Companion"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TrackingScriptsLoader />
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <RouteChangeTracker />
          <AuthProvider>
            <RecordingProvider>
              <RecordingBar />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/landing-legacy" element={<LandingPageLegacy />} />
                <Route path="/login" element={<Login />} />
                <Route path="/cadastro" element={<Cadastro />} />
                <Route path="/esqueci-senha" element={<EsqueciSenha />} />
                <Route path="/redefinir-senha" element={<RedefinirSenha />} />
                <Route path="/privacidade" element={<Privacidade />} />
                <Route path="/termos" element={<TermosUso />} />
                <Route path="/lgpd" element={<LGPD />} />
                <Route path="/oferta-indicacao" element={<OfertaIndicacao />} />
                <Route path="/r/:slug" element={<ReferralRedirect />} />
                <Route path="/ajuda" element={<Ajuda />} />
                <Route path="/status" element={<Status />} />
                <Route path="/blog" element={<BlogIndex />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route
                  path="/consultas"
                  element={
                    <ProtectedRoute>
                      <Consultas />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/templates"
                  element={
                    <ProtectedRoute>
                      <Templates />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/plano"
                  element={
                    <ProtectedRoute>
                      <Plano />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/perfil"
                  element={
                    <ProtectedRoute>
                      <Perfil />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dispositivos"
                  element={
                    <ProtectedRoute>
                      <Dispositivos />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <Admin />
                    </ProtectedRoute>
                  }
                />
                <Route path="/prontuario" element={<Prontuario />} />
                <Route
                  path="/companion"
                  element={
                    <ProtectedRoute>
                      <Companion />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </RecordingProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
